import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { startInteraction } from './auth/interaction-lock';
import {
  AccountInfo,
  EventMessage,
  EventType,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { Observable, BehaviorSubject, filter, takeUntil, Subject, map } from 'rxjs';
import { JwtUtilsService } from './services/jwt-utils.service';
import {
  VERICURE_API_SCOPE,
  GRAPH_SCOPES,
  requireEntraAuth,
  loginScopes,
  isInteractionRequired,
  redirectForApiScope,
} from './auth/api-scope';
import { VericureUser } from './models/vericure-user';
import {
  describeActiveAccount,
  forgetActiveAccount,
  rememberActiveAccount,
  resolveActiveAccount,
} from './auth/active-account';
import { AuthStatusService } from './auth/auth-status.service';
import { ActiveCustomer } from './models/active-customer';
import { ExternalIdSignInService } from './auth/external-id-sign-in.service';

const ROLES_STORAGE_KEY = 'vericureUserRoles';
const ACTIVE_CUSTOMER_KEY = 'vericureActiveCustomer';
const AVAILABLE_CUSTOMERS_KEY = 'vericureAvailableCustomers';

@Injectable({
  providedIn: 'root',
})
export class UserAuthService {
  private _isAuthenticated = new BehaviorSubject<boolean>(false);
  isAuthenticated$: Observable<boolean> = this._isAuthenticated.asObservable();
  private _activeAccount = new BehaviorSubject<AccountInfo | null>(null);
  activeAccount$: Observable<AccountInfo | null> =
    this._activeAccount.asObservable();

  UserName: string = '';
  loginDisplay = false;
  tokenExpiration: string = '';
  CustomerTenantId: string = '';
  authProvider: string = ''; // Track which provider is being used
  private readonly _destroying$ = new Subject<void>();

  private _userRoles = new BehaviorSubject<string[]>(this.loadCachedRoles());
  userRoles$: Observable<string[]> = this._userRoles.asObservable();
  isAdmin$: Observable<boolean> = this._userRoles.pipe(
    map((roles) => roles.includes('administrator') || roles.includes('SuperAdmin'))
  );
  isSuperAdmin$: Observable<boolean> = this._userRoles.pipe(
    map((roles) => roles.includes('SuperAdmin'))
  );

  private _activeCustomer = new BehaviorSubject<ActiveCustomer | null>(this.loadActiveCustomer());
  activeCustomer$: Observable<ActiveCustomer | null> = this._activeCustomer.asObservable();

  availableCustomers: ActiveCustomer[] = this.loadAvailableCustomers();

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private jwtUtils: JwtUtilsService,
    private authStatus: AuthStatusService,
    private externalId: ExternalIdSignInService
  ) {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
      });

    // ANCHOR THE SESSION TO THE ACCOUNT THE USER CHOSE.
    //
    // Nothing in this application had ever called setActiveAccount, so
    // getActiveAccount() always answered null and every caller fell through to
    // getAllAccounts()[0] — cache order, not the user's choice. With two cached
    // identities that silently acted as the wrong one: the silent authorize
    // request went out anchored to joel@vericure.io while the user was signing in
    // as joel@vortexcompanies.com, and came back refused.
    //
    // The login response is the only place that knows which identity was picked,
    // so it is recorded the moment it arrives.
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.SSO_SILENT_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((msg) => {
        const payload = msg.payload as { account?: AccountInfo; expiresOn?: string };
        if (payload?.account) {
          rememberActiveAccount(this.msalService.instance, payload.account);
          // Re-read: the display and CustomerTenantId were computed from
          // whatever account was current BEFORE this one was established.
          this.setLoginDisplay();
        }
        if (msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && payload?.expiresOn) {
          this.tokenExpiration = payload.expiresOn;
          localStorage.setItem('tokenExpiration', this.tokenExpiration);
        }
      });

    // EXTERNAL ID (CIAM) SESSIONS. A CIAM account lives in its own MSAL instance (sessionStorage,
    // external-id-sign-in.service.ts), so nothing above ever sees it. Re-evaluate whenever it
    // changes (popup sign-in, sign-out), and restore one this tab already holds, so a reload inside
    // the tab does not bounce a CIAM user to /login. restore() is a no-op when CIAM is unconfigured.
    this.externalId.account$
      .pipe(takeUntil(this._destroying$))
      .subscribe(() => this.setLoginDisplay());
    if (this.externalId.enabled()) {
      this.externalId.restore().catch(() => undefined);
    }

    // Initialize authentication state on service startup
    // Only call setLoginDisplay after checking if MSAL is ready
    this.initializeAuthState();
  }

  /**
   * Set the user's roles from the Cosmos VericureUsers document returned by CheckUserExists.
   * Roles are persisted to localStorage so they survive page reloads while logged in.
   */
  setRolesFromCosmos(user: VericureUser | null): void {
    const roles = user?.roles ?? [];
    this._userRoles.next(roles);
    try {
      if (roles.length > 0) {
        localStorage.setItem(ROLES_STORAGE_KEY, JSON.stringify(roles));
      } else {
        // AN EMPTY ROLES ARRAY IS NOT WORTH REMEMBERING.
        //
        // Caching [] made a single bad answer permanent: every later load read
        // it back as though the question had been settled, when in fact nothing
        // had been established at all. Removing the key means the next load asks
        // again, which is the correct behaviour for "we do not know".
        //
        // A genuine denial still routes to /unauthorized for THIS session — the
        // in-memory value above carries it — it just does not survive a reload
        // as a fact.
        localStorage.removeItem(ROLES_STORAGE_KEY);
      }
    } catch {
      // ignore storage errors
    }

    // customer-role users have exactly one customer identified in their VericureUser doc.
    // CheckUserExists uses OkObjectResult (camelCase) so the field arrives as "customerId".
    // Cast defensively to also handle any PascalCase variant for robustness.
    const userCustomerId: string = user?.customerId ?? (user as any)?.CustomerId ?? '';
    if (roles.includes('customer') && userCustomerId) {
      this.setActiveCustomer({
        CustomerId: userCustomerId,
        CustomerName: '',
        TenantId: user?.TenantId ?? this.CustomerTenantId,
        TenantName: '',
      });
    }
  }

  private clearRoles(): void {
    this._userRoles.next([]);
    try {
      localStorage.removeItem(ROLES_STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
  }

  setActiveCustomer(customer: ActiveCustomer): void {
    this._activeCustomer.next(customer);
    try {
      sessionStorage.setItem(ACTIVE_CUSTOMER_KEY, JSON.stringify(customer));
    } catch { /* ignore */ }
  }

  getActiveCustomer(): ActiveCustomer | null {
    return this._activeCustomer.getValue();
  }

  /**
   * The tenant the user AUTHENTICATED against — from the signed-in account, not
   * from a customer row. This is the only value that can legitimately default a
   * customer selection; a customer's own TenantId is what we are choosing between.
   */
  getSignedInTenantId(): string {
    return resolveActiveAccount(this.msalService.instance)?.tenantId ?? '';
  }

  getActiveCustomerId(): string {
    return this._activeCustomer.getValue()?.CustomerId ?? '';
  }

  setAvailableCustomers(customers: ActiveCustomer[]): void {
    this.availableCustomers = customers;
    try {
      sessionStorage.setItem(AVAILABLE_CUSTOMERS_KEY, JSON.stringify(customers));
    } catch { /* ignore */ }
  }

  private clearActiveCustomer(): void {
    this._activeCustomer.next(null);
    this.availableCustomers = [];
    try {
      sessionStorage.removeItem(ACTIVE_CUSTOMER_KEY);
      sessionStorage.removeItem(AVAILABLE_CUSTOMERS_KEY);
    } catch { /* ignore */ }
  }

  private isValidCustomer(c: ActiveCustomer | null | undefined): boolean {
    return !!c && !!c.CustomerId && !!c.TenantId;
  }

  private loadActiveCustomer(): ActiveCustomer | null {
    try {
      const stored = sessionStorage.getItem(ACTIVE_CUSTOMER_KEY);
      if (!stored) return null;
      const parsed = JSON.parse(stored) as ActiveCustomer;
      // Discard malformed cache (e.g. pre-fix entries missing ids) so the app re-fetches.
      if (!this.isValidCustomer(parsed)) {
        sessionStorage.removeItem(ACTIVE_CUSTOMER_KEY);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private loadAvailableCustomers(): ActiveCustomer[] {
    try {
      const stored = sessionStorage.getItem(AVAILABLE_CUSTOMERS_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      // Drop malformed entries; if any are bad, discard the whole cache to force a clean re-fetch.
      if (!parsed.every((c) => this.isValidCustomer(c))) {
        sessionStorage.removeItem(AVAILABLE_CUSTOMERS_KEY);
        return [];
      }
      return parsed;
    } catch {
      return [];
    }
  }

  private loadCachedRoles(): string[] {
    try {
      const cached = localStorage.getItem(ROLES_STORAGE_KEY);
      if (!cached) return [];
      const parsed = JSON.parse(cached);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  getRoles(): string[] {
    return this._userRoles.getValue();
  }

  /** The MSAL account currently anchored, or null. Read by RolesResolverService. */
  getActiveAccountInfo(): AccountInfo | null {
    return this._activeAccount.getValue();
  }

  /**
   * The identity pair (tenantId, localAccountId = oid) of WHICHEVER session is signed in: the
   * workforce account, else the External ID account. For callers that ask the server about the
   * signed-in user (CheckUserExists); the server takes identity from the bearer either way, so this
   * only has to name the same user. Not for anything that means "the workforce account".
   */
  getSessionAccountInfo(): AccountInfo | null {
    const workforce = this._activeAccount.getValue();
    if (workforce) {
      return workforce;
    }
    return this.isExternalIdAuthenticated() ? this.externalId.account() : null;
  }

  hasRole(role: string): boolean {
    return this._userRoles.getValue().includes(role);
  }

  // Safely initialize authentication state
  private initializeAuthState(): void {
    try {
      // Try to set login display, but handle the case where MSAL might not be ready
      this.setLoginDisplay();
    } catch (error) {
      console.log('UserAuthService > MSAL not yet initialized, will retry when ready');
      // The inProgress$ subscription will handle this when MSAL is ready
    }
  }

  // If the user is logged in, present the user with a "logged in" experience
  setLoginDisplay() {
    try {
      // resolveActiveAccount, NOT getAllAccounts()[0]. That subscript is the
      // whole bug: it returns whichever account the MSAL cache happens to list
      // first, which with two signed-in identities is not the one the user chose,
      // and it is what every downstream call — CheckUserExists' tenantId and
      // objectId included — was keyed to.
      // describeActiveAccount, not resolveActiveAccount: the UI needs the REASON.
      // "signed out" and "two accounts, cannot tell which" both give a null
      // account and need completely different words in front of the user.
      const resolution = describeActiveAccount(this.msalService.instance);
      const account = resolution.account;

      if (resolution.kind === 'ambiguous') {
        this.authStatus.ambiguous(resolution.candidates);
      } else if (account) {
        this.authStatus.signedIn(account);
      }
      // 'unavailable' is deliberately NOT published. MSAL throws while it is
      // still initialising, and publishing signed-out there would flash "Not
      // signed in" on every load of a perfectly good session.

      if (account) {
        // Microsoft/MSAL authentication
        this._activeAccount.next(account);
        this._isAuthenticated.next(true);
        this.UserName = account.username;
        this.authProvider = 'microsoft';

        // Extract tenant ID from the account
        this.CustomerTenantId = this.getCustomerTenantIdFromAccount(account);

        console.log('UserAuthService > Microsoft Account:', account.username);
        console.log('UserAuthService > Customer Tenant ID:', this.CustomerTenantId);
      } else {
        // NO CLIENT-SIDE TRUST.
        //
        // This branch used to accept `localStorage.isAuthenticated === 'true'`
        // plus any non-empty `accessToken` as proof of identity, and took
        // CustomerTenantId straight from localStorage. Both are writable by
        // anyone with devtools, and the "token" was a client-minted string like
        // `temp_token_<timestamp>` — so the check amounted to "the browser says
        // so". The tenant id then went into the query string of every API call.
        //
        // It bought nothing even when honest: a non-MSAL session has no account,
        // so the interceptor attaches no bearer and DataServices refuses every
        // data call under RequireEntraAuth. The only thing the trust achieved was
        // letting a forged session past the UI guard into an app that could not
        // load anything.
        //
        // Identity now comes from MSAL alone. Google returns here once its ID
        // token is validated and a tenant is derived from a verified claim.
        //
        // EXTERNAL ID IS MSAL TOO, a second instance. Its account is a genuine AccountInfo issued by
        // that instance after a real sign-in, not a client-side flag, so it counts. It is NOT pushed
        // into _activeAccount: that stream means the WORKFORCE account, and its consumers (tenant id,
        // account switching) would act on the wrong directory.
        //
        // CustomerTenantId IS the CIAM account's tenant (c46c90fe), NOT empty. This used to say "stays
        // empty; the server takes the tenant from the bearer", which was wrong: GetCustomers required it
        // and answered 400, which was Joel's "Failed to load customers" on 2026-10-05. The tenant only
        // proves MEMBERSHIP server-side; every External customer shares it, so scope always comes from the
        // caller's own customerIds (DataServices ExternalCustomerScopeTests).
        const ciam = this.externalId.account();
        if (ciam) {
          this._activeAccount.next(null);
          this._isAuthenticated.next(true);
          this.UserName = ciam.username;
          this.CustomerTenantId = ciam.tenantId ?? '';
          this.authProvider = 'externalid';
          // THE HEADER READS AuthStatusService, and nothing on the CIAM path ever wrote it: every
          // External ID session showed "Signed out / Not signed in." while it worked (Joel,
          // 2026-10-08). Same rule as the workforce branch above: a real account is signed in.
          this.authStatus.signedIn(ciam);
          console.log('UserAuthService > External ID account:', ciam.username);
          return;
        }
        this._activeAccount.next(null);
        this._isAuthenticated.next(false);
        this.CustomerTenantId = '';
        this.authProvider = '';
        if (resolution.kind === 'none') {
          this.authStatus.signedOut();
        }
        console.log('UserAuthService > No accounts found.');
      }
    } catch (error) {
      // MSAL NOT READY IS NOT AUTHENTICATED. This block used to fall back to the
      // same localStorage flags, which made the weakest path also the one taken
      // whenever MSAL happened to be slow — a race decided in favour of the
      // forgeable check.
      console.log('UserAuthService > MSAL not ready; not authenticated yet');
      this._activeAccount.next(null);
      this._isAuthenticated.next(false);
    }
  }

  // Log the user in and redirect them if MSAL provides a redirect URI otherwise go to the default URI
  login() {
    // Force the account picker so multi-account users can choose which
    // identity to sign in with, rather than silently SSO-ing to whichever
    // Microsoft session happens to be active in the browser.
    //
    // SCOPES MATTER HERE. MSALGuardConfigFactory's authRequest is NOT applied to
    // this call: MsalGuard is not `canActivate` on any route (routes use
    // userAuthGuard), and this call does not spread msalGuardConfig.authRequest.
    // So whatever is asked for here is the ONLY thing consented and cached at
    // sign-in. Omitting the API scope left it un-consented, and the interceptor's
    // later acquireTokenSilent for it failed (interaction_required) and fell back
    // to the function key — no Authorization header, which is exactly what the
    // P2 canary saw.
    // DO NOT CLEAR THE ANCHOR HERE. This used to call forgetActiveAccount()
    // before redirecting, on the reasoning that holding the previous choice while
    // the picker is open could silently keep the old identity. The reasoning was
    // sound and the placement was wrong, and it cost a double login:
    //
    //   forgetActiveAccount() removes vericureActiveHomeAccountId, then MSAL
    //   redirects. On the return leg setLoginDisplay() runs describeActiveAccount()
    //   before LOGIN_SUCCESS has been processed, so there are TWO cached accounts,
    //   no persisted anchor and no MSAL active account yet. That is precisely the
    //   'ambiguous' case, active-account.ts correctly refuses to guess, and
    //   _isAuthenticated goes false — so userAuthGuard bounces the user back to
    //   /login and they sign in a second time. We manufactured the ambiguity and
    //   then correctly declined to resolve it.
    //
    // The old anchor is safe to keep across the redirect because the LOGIN_SUCCESS
    // handler OVERWRITES it with the account the user actually chose, and MSAL
    // events are replayed (MSAL_BROADCAST_CONFIG eventsToReplay), so that handler
    // cannot be missed. Re-anchoring happens on the way IN, not on the way OUT.
    //
    // If the user abandons the picker, keeping the previous anchor is also the
    // right outcome: nothing was chosen, so nothing should change.
    // Wrapped because this call THREW for Joel and nothing caught it:
    // BrowserAuthError interaction_in_progress, left behind by an abandoned
    // consent screen. startInteraction clears a stale flag and retries once.
    startInteraction(
      () =>
        this.msalService.loginRedirect({
          prompt: 'select_account',
          scopes: loginScopes(),
        } as RedirectRequest),
      'loginRedirect'
    );
  }

  /**
   * Microsoft sign-in for an email the user already TYPED on /login (home-realm discovery).
   *
   * `loginHint` prefills the address and `domainHint` lets Entra go straight to that company's
   * sign-in. NO `prompt`: with prompt=select_account the account picker comes back, which is the
   * extra step the one-box login exists to remove. Someone already signed in as that account may
   * pass straight through.
   */
  loginWithHint(email: string, domain: string): void {
    startInteraction(
      () =>
        this.msalService.loginRedirect({
          scopes: loginScopes(),
          loginHint: email,
          domainHint: domain || undefined,
        } as RedirectRequest),
      'loginRedirect'
    );
  }

  /**
   * Microsoft sign-in that RETURNS TO `path`, for the invite and bind landing pages.
   *
   * login() relies on MSAL's default return: the URL the redirect started from.
   * The landing pages strip their link from the URL before signing in, and the
   * return leg passes through the app root and its guard, so an explicit
   * redirectStartPage makes the destination deterministic rather than inferred.
   */
  loginTo(path: string): void {
    const start = new URL(path, window.location.origin).toString();
    startInteraction(
      () =>
        this.msalService.loginRedirect({
          prompt: 'select_account',
          scopes: loginScopes(),
          redirectStartPage: start,
        } as RedirectRequest),
      'loginRedirect'
    );
  }

  // Log the user out
  logout() {
    const currentAuthProvider = this.authProvider || localStorage.getItem('authProvider');
    
    // Clear localStorage authentication data for all login types
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('username');
    localStorage.removeItem('customerTenantId');
    localStorage.removeItem('tokenExpiration');
    localStorage.removeItem('authProvider');
    
    // Clear leftovers from the removed Google stub. Kept deliberately: browsers that
    // used the old flow still carry these keys, and this is the only thing that clears them.
    localStorage.removeItem('googleUserName');
    localStorage.removeItem('googleUserPicture');
    localStorage.removeItem('google_id_token');
    localStorage.removeItem('google_user');
    localStorage.removeItem('google_oauth_nonce');

    // Clear cached roles, the chosen identity, and active customer selection
    forgetActiveAccount();
    this.authStatus.signedOut();
    this.clearRoles();
    this.clearActiveCustomer();
    
    // External ID: end the CIAM session, in this tab AND at login.vericure.io, BEFORE leaving, or
    // its token keeps flowing to the API from the interceptor after "Log out". Fire-and-forget is not
    // enough: a navigation would race the cache clear. When signOut() has started the end-session
    // redirect, that IS the navigation (it comes back to the origin, whose guard sends a signed-out
    // user to /login); navigating to /login as well would cut it short and leave the IdP session up,
    // which is how a "signed out" user got straight back in without a password (2026-10-08).
    if (currentAuthProvider === 'externalid') {
      const done = (redirecting: boolean) => {
        this._activeAccount.next(null);
        this._isAuthenticated.next(false);
        this.UserName = '';
        this.CustomerTenantId = '';
        this.authProvider = '';
        if (!redirecting) this.navigateToLogin();
      };
      this.externalId.signOut().then(done, () => done(false));
      return;
    }

    // Clear MSAL authentication (this will redirect for Microsoft users)
    if (currentAuthProvider === 'microsoft' || !currentAuthProvider) {
      console.log('Logging out from Microsoft authentication');
      this.msalService.logoutRedirect();
    } else {
      // For non-Microsoft users, just clear the state and redirect manually
      this._activeAccount.next(null);
      this._isAuthenticated.next(false);
      this.UserName = '';
      this.CustomerTenantId = '';
      this.authProvider = '';
      
      // Redirect to login page
      this.navigateToLogin();
    }
  }

  /**
   * THE ONE FULL-PAGE NAVIGATION logout() makes, behind a method so a spec can stub it.
   *
   * A full-page load is deliberate: it drops every in-memory session. But inside Karma the same
   * assignment navigates the TEST PAGE away. The client never reports again, and the whole run dies
   * on browserNoActivityTimeout, discarding every spec not yet reached. That made the CI suite
   * abort at a random point on every run (random spec order). Specs that reach logout() on a
   * non-Microsoft branch spy on this method instead.
   */
  navigateToLogin(): void {
    window.location.href = '/login';
  }

  isAuthenticated(): boolean {
    // Check MSAL authentication first
    // MSAL is the only authority. The localStorage flags this used to fall back
    // on were writable by the user, so the guard protecting every route could be
    // satisfied from devtools.
    return this._isAuthenticated.getValue();
  }

  /**
   * Extract customer tenant ID from the account information
   * @param account - The account info from MSAL
   * @returns The customer tenant ID
   */
  getCustomerTenantIdFromAccount(account: AccountInfo): string {
    // Method 1: From account homeAccountId (format: objectId.tenantId)
    if (account.homeAccountId && account.homeAccountId.includes('.')) {
      const parts = account.homeAccountId.split('.');
      if (parts.length >= 2) {
        return parts[1]; // The second part is the tenant ID
      }
    }

    // Method 2: From account environment (if available)
    if (account.environment) {
      // This is less reliable but can be used as fallback
      console.log('Account environment:', account.environment);
    }

    // Method 3: From account localAccountId (if it contains tenant info)
    if (account.localAccountId) {
      console.log('Local Account ID:', account.localAccountId);
    }

    return '';
  }

  /**
   * Returns the tenant ID to use for API calls.
   * After customer selection, returns the selected customer's tenantId.
   * Falls back to the caller's own tenantId before customer selection completes.
   */
  getCurrentCustomerTenantId(): string {
    return this._activeCustomer.getValue()?.TenantId ?? this.CustomerTenantId;
  }

  /**
   * Extract customer tenant ID from JWT token claims
   * @param token - The JWT token (access token or ID token)
   * @returns The customer tenant ID from the token
   */
  getCustomerTenantIdFromToken(token: string): string {
    try {
      // JWT tokens have three parts separated by dots: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('Invalid JWT token format');
        return '';
      }

      // Decode the payload (second part)
      const payload = parts[1];
      
      // Add padding if needed for base64 decoding
      const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
      
      // Decode base64
      const decodedPayload = atob(paddedPayload);
      
      // Parse JSON
      const claims = JSON.parse(decodedPayload);
      
      // Extract tenant ID from various possible claims
      return claims.tid || claims.tenant_id || claims.tenantId || '';
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return '';
    }
  }

  /**
   * Get customer tenant ID from the current access token
   * @returns Promise<string> - The customer tenant ID from the current access token
   */
  async getCustomerTenantIdFromCurrentToken(): Promise<string> {
    try {
      const account = resolveActiveAccount(this.msalService.instance);
      if (!account) {
        console.error('No active account found');
        return '';
      }

      // Graph-scoped deliberately: this reads `tid` out of a Graph token. An
      // access token is issued for ONE resource, so the API scope must NOT be
      // mixed in here — it would change the audience and break this lookup. Use
      // acquireApiAccessToken() when you want a token for our API.
      const tokenRequest = {
        scopes: [...GRAPH_SCOPES],
        account: account
      };

      const response = await this.msalService.instance.acquireTokenSilent(tokenRequest);
      
      if (response.accessToken) {
        return this.getCustomerTenantIdFromToken(response.accessToken);
      }
      
      return '';
    } catch (error) {
      console.error('Error getting customer tenant ID from current token:', error);
      return '';
    }
  }

  /**
   * Acquire an access token for the Vericure API (audience = the 'Vericure API'
   * app registration), for callers that need the bearer outside MsalInterceptor.
   *
   * Returns '' when the flag is off, when there is no MSAL account (the user
   * signed in with Google or email/password), or when silent acquisition fails —
   * callers fall back to the function-key path rather than forcing interaction.
   */
  async acquireApiAccessToken(): Promise<string> {
    if (!requireEntraAuth()) {
      return '';
    }
    try {
      const account = resolveActiveAccount(this.msalService.instance);
      if (!account) {
        // Non-MSAL provider (Google / email+password), or two accounts with no
        // choice on record — no token can be acquired for a user we cannot name.
        return '';
      }
      const response = await this.msalService.instance.acquireTokenSilent({
        scopes: [VERICURE_API_SCOPE],
        account,
      });
      return response.accessToken ?? '';
    } catch (error) {
      console.error('Error acquiring Vericure API access token:', error);
      this.maybeRedirectForApiScope(error);
      return '';
    }
  }

  /**
   * Opt-in recovery for an MSAL account whose cached grant predates the API scope
   * being added to the login request.
   *
   * Fires ONLY when: the flag is on, the error genuinely needs interaction
   * (interaction_required / login_required / consent_required — never
   * invalid_scope, which is a config fault a redirect cannot fix), and we have not
   * already redirected once this session. The once-per-session latch is what stops
   * a misconfigured scope turning into a redirect loop.
   *
   * Structurally unreachable for Google / email+password sessions: the only caller
   * has already returned early when there is no MSAL account.
   */
  private maybeRedirectForApiScope(error: unknown): void {
    if (!requireEntraAuth() || !isInteractionRequired(error)) {
      return;
    }
    // Shared with the interceptor so both paths draw on the same per-account
    // budget and both return the user to where they were. The account is passed
    // for the same reason it is there: an unhinted redirect against /common can
    // repair the wrong identity's grant and leave this one exactly as broken.
    const account = resolveActiveAccount(this.msalService.instance) ?? undefined;
    redirectForApiScope(this.msalService.instance, 'UserAuth', account);
  }

  /**
   * Get customer tenant ID from the current ID token
   * @returns The customer tenant ID from the current ID token
   */
  getCustomerTenantIdFromIdToken(): string {
    const account = resolveActiveAccount(this.msalService.instance);
    if (!account || !account.idToken) {
      console.error('No active account or ID token found');
      return '';
    }

    return this.getCustomerTenantIdFromToken(account.idToken);
  }

  /**
   * Get the current authentication provider
   * @returns string - The authentication provider ('microsoft', 'google', 'email', or '')
   */
  getAuthProvider(): string {
    return this.authProvider;
  }

  /**
   * Get the authenticated user's objectId across all auth providers.
   * - MSAL: first segment of homeAccountId (format: objectId.tenantId)
   * - Google/JWT: oid or sub claim from the stored access token
   * - Email/password: stored username (email) as stable identifier
   */
  getObjectId(): string {
    const account = this._activeAccount.getValue();
    if (account?.homeAccountId?.includes('.')) {
      return account.homeAccountId.split('.')[0];
    }

    // An External ID session has no workforce account; its own oid is the CIAM account's
    // localAccountId (the same value the server reads from the bearer, so a supplied objectId matches).
    if (!account && this.isExternalIdAuthenticated()) {
      return this.externalId.account()?.localAccountId ?? '';
    }

    // No localStorage fallback. Nothing writes `accessToken` any more, and
    // reading an oid out of whatever a user planted there would put an
    // attacker-chosen objectId on every API call. The server takes identity from
    // the bearer's oid claim regardless, so this only ever risked confusion.
    return '';
  }

  /**
   * Check if user is authenticated with Google
   * @returns boolean - True if authenticated with Google
   */
  isGoogleAuthenticated(): boolean {
    return this.authProvider === 'google' && this._isAuthenticated.getValue();
  }

  /**
   * Check if user is authenticated with Microsoft
   * @returns boolean - True if authenticated with Microsoft
   */
  isMicrosoftAuthenticated(): boolean {
    return this.authProvider === 'microsoft' && this._isAuthenticated.getValue();
  }

  /** True when the session is an External ID (CIAM) account in the second MSAL instance. */
  isExternalIdAuthenticated(): boolean {
    return this.authProvider === 'externalid' && this._isAuthenticated.getValue();
  }

  /**
   * Check if user is authenticated with email/password
   * @returns boolean - True if authenticated with email/password
   */
  isEmailAuthenticated(): boolean {
    return this.authProvider === 'email' && this._isAuthenticated.getValue();
  }

  /**
   * Get Google user information if available
   * @returns object with Google user data or null
   */
  getGoogleUserInfo(): { name: string; picture: string; email: string } | null {
    if (this.authProvider === 'google') {
      return {
        name: localStorage.getItem('googleUserName') || '',
        picture: localStorage.getItem('googleUserPicture') || '',
        email: localStorage.getItem('username') || ''
      };
    }
    return null;
  }
}
